import { useState, type FormEvent } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import type { ProjectAddress } from '../../../planning/types';
import { ADDRESS_MODAL_LABELS as LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';

interface AddressModalProps {
  address?: ProjectAddress;
  onClose: () => void;
  onSave: (name: string, street: string, houseNumber: string, postalCode: string, city: string) => Promise<boolean>;
}

export default function AddressModal({ address, onClose, onSave }: AddressModalProps) {
  useModalHistory(onClose);
  const [name, setName] = useState(address?.name ?? '');
  const [street, setStreet] = useState(address?.street ?? '');
  const [houseNumber, setHouseNumber] = useState(address?.house_number ?? '');
  const [postalCode, setPostalCode] = useState(address?.postal_code ?? '');
  const [city, setCity] = useState(address?.city ?? '');
  const [errors, setErrors] = useState({ name: '', street: '', houseNumber: '', postalCode: '', city: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const newErrors = {
      name: !name.trim() ? LABELS.nameRequired : '',
      street: !street.trim() ? LABELS.streetRequired : '',
      houseNumber: !houseNumber.trim() ? LABELS.houseNumberRequired : '',
      postalCode: !postalCode.trim() ? LABELS.postalCodeRequired : '',
      city: !city.trim() ? LABELS.cityRequired : '',
    };
    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    const ok = await onSave(name.trim(), street.trim(), houseNumber.trim(), postalCode.trim(), city.trim());
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={address ? LABELS.editTitle : LABELS.createTitle} confirmLabel={LABELS.save} confirmFormId="address-form" confirmDisabled={saving} confirmLoading={saving} testId="address-modal">
      <form id="address-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <TextField
            label={LABELS.nameLabel}
            value={name}
            onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((prev) => ({ ...prev, name: '' })); }}
            required
            autoFocus
            fullWidth
            className="mt-2"
            error={!!errors.name}
            helperText={errors.name || undefined}
          />
          <div className="flex gap-3">
            <TextField
              label={LABELS.streetLabel}
              value={street}
              onChange={(e) => { setStreet(e.target.value); if (errors.street) setErrors((prev) => ({ ...prev, street: '' })); }}
              required
              fullWidth
              error={!!errors.street}
              helperText={errors.street || undefined}
            />
            <TextField
              label={LABELS.houseNumberLabel}
              value={houseNumber}
              onChange={(e) => { setHouseNumber(e.target.value); if (errors.houseNumber) setErrors((prev) => ({ ...prev, houseNumber: '' })); }}
              required
              className="min-w-24"
              error={!!errors.houseNumber}
              helperText={errors.houseNumber || undefined}
            />
          </div>
          <div className="flex gap-3">
            <TextField
              label={LABELS.postalCodeLabel}
              value={postalCode}
              onChange={(e) => { setPostalCode(e.target.value); if (errors.postalCode) setErrors((prev) => ({ ...prev, postalCode: '' })); }}
              required
              className="min-w-24"
              error={!!errors.postalCode}
              helperText={errors.postalCode || undefined}
            />
            <TextField
              label={LABELS.cityLabel}
              value={city}
              onChange={(e) => { setCity(e.target.value); if (errors.city) setErrors((prev) => ({ ...prev, city: '' })); }}
              required
              fullWidth
              error={!!errors.city}
              helperText={errors.city || undefined}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
